import { Extension, InputRule } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Slice, Fragment, Node } from 'prosemirror-model';
import escapeRegExp from 'lodash/escapeRegExp';

/**
 * Transforms HTML to split paragraphs at <br> tags
 * Converts <p>Zone<br>Ztwo<br>Zthree</p> into <p>Zone</p><p>Ztwo</p><p>Zthree</p>
 * We convert <br> to <p> as we double space our <p>  with empty <p>
 */
function transformHtmlWithBrTags(html: string): string {
  if (!html || !html.includes('<br')) {
    return html;
  }

  // Match paragraph tags and their content, including nested tags
  // This regex captures: <p...>content</p> where content may include <br> tags
  return html.replace(
    /<p([^>]*)>([\s\S]*?)<\/p>/gi,
    (match, pAttrs, content) => {
      // Check if content contains <br> tags (handles <br>, <br/>, <br />, <br style="...">, etc.)
      if (!/<br\s*[^>]*\/?>/i.test(content)) {
        return match; // No <br> tags, return original paragraph
      }

      // Split content at <br> tags (handles various formats: <br>, <br/>, <br />, <br style="...">, etc.)
      const parts = content.split(/<br\s*[^>]*\/?>/i);

      // Create separate paragraphs for each part
      return parts
        .map(part => {
          const trimmed = part.trim();
          // If empty, create empty paragraph, otherwise wrap content
          return trimmed ? `<p${pAttrs}>${trimmed}</p>` : `<p${pAttrs}></p>`;
        })
        .join('');
    }
  );
}

/**
 * Inserts extra newline when pasting paragraphs and handles image paste and drop
 */
const createPastePlugin = (onImagePaste?: ((files: File[]) => void) | null) =>
  new Plugin({
    key: new PluginKey('DaDamzPastePlugin'),
    props: {
      // Transform HTML before ProseMirror parses it to split paragraphs at <br> tags
      // Only do this for internal pastes (containing 'devioussans')
      transformPastedHTML: (html: string) => {
        // Only transform if it's an internal paste
        if (html.includes('devioussans')) {
          return transformHtmlWithBrTags(html);
        }
        return html;
      },
      handlePaste: (view, event, slice) => {
        const { schema } = view.state;
        const paragraphType = schema.nodes.paragraph;

        // Check for image files in clipboard
        const { files = [] } = event.clipboardData || {};
        const imageFiles = Array.from(files);

        if (imageFiles.length > 0 && onImagePaste) {
          onImagePaste(imageFiles);
          return true; // We handled it
        }

        const plainText = event.clipboardData?.getData('text/plain') ?? '';
        const htmlContent = event.clipboardData?.getData('text/html') ?? '';

        // Handle pasting long text
        if (plainText.length > 2_000) {
          const trimmedText = plainText.slice(0, 2_000);
          const textNode = schema.text(trimmedText);
          const newSlice = new Slice(Fragment.from(textNode), 0, 0);
          const tr = view.state.tr.replaceSelection(newSlice);
          view.dispatch(tr);
          return true;
        }

        // Check if it's plaintext only (no HTML content or HTML is just basic wrapping)
        const isPlaintextPaste =
          plainText.length > 0 &&
          (htmlContent.length === 0 ||
            (htmlContent.includes('<') &&
              htmlContent.replace(/<[^>]*>/g, '').trim() === plainText));

        // If it's plaintext paste, don't add extra paragraphs
        if (isPlaintextPaste) {
          return false;
        }

        const isInternalPaste = htmlContent.includes('devioussans');

        const nodes: Node[] = [];
        let size = 0;
        let hasChanges = false;
        slice.content.forEach((node, offset, index) => {
          size += node.textContent.length;
          if (size > 2_000) {
            return;
          }

          nodes.push(node);

          // Insert an extra empty paragraph after each paragraph (except the last one)
          if (
            node.type === paragraphType &&
            index < slice.content.childCount - 1 &&
            !isInternalPaste
          ) {
            hasChanges = true;
            const emptyParagraph = paragraphType.createAndFill();
            if (emptyParagraph) {
              nodes.push(emptyParagraph);
            }
          }
        });

        if (!hasChanges) {
          return false;
        }

        const newFragment = Fragment.fromArray(nodes);
        const newSlice = new Slice(newFragment, slice.openStart, slice.openEnd);
        const tr = view.state.tr.replaceSelection(newSlice);
        view.dispatch(tr);
        return true;
      },
      handleDrop: (view, event) => {
        // Check for dropped files
        const { files = [] } = event.dataTransfer || {};
        const imageFiles = Array.from(files);

        if (imageFiles.length > 0 && onImagePaste) {
          onImagePaste(imageFiles);
          return true; // We handled it
        }

        return false; // Let it pass through
      },
    },
  });

const emoteMap = {
  '>:(': '😠',
  '>:-(': '😠',
  ':")': '😊',
  ':-")': '😊',
  '</3': '💔',
  '<\\3': '💔',
  ':/': '😕',
  ':-/': '😕',
  ":'(": '😢',
  ":'-(": '😢',
  ':,(': '😢',
  ':,-(': '😢',
  ':(': '☹️',
  ':-(': '☹️',
  '<3': '❤️',
  ']:(': '😈',
  ']:-(': '😈',
  'o:)': '😇',
  'O:)': '😇',
  'o:-)': '😇',
  'O:-)': '😇',
  '0:)': '😇',
  '0:-)': '😇',
  ":')": '😂',
  ":'-)": '😂',
  ':,)': '😂',
  ':,-)': '😂',
  ":'D": '😂',
  ":'-D": '😂',
  ':,D': '😂',
  ':,-D': '😂',
  ':*': '😘',
  ':-*': '😘',
  'x-)': '😆',
  'X-)': '😆',
  ':|': '😐',
  ':-|': '😐',
  ':o': '😮',
  ':-o': '😮',
  ':O': '😮',
  ':-O': '😮',
  ':@': '😡',
  ':-@': '😡',
  ':D': '😀',
  ':-D': '😀',
  ':)': '🙂',
  ':-)': '🙂',
  ']:)': '😏',
  ']:-)': '😏',
  ":,'(": '😭',
  ":,'-(": '😭',
  ';(': '😭',
  ';-(': '😭',
  ':P': '😛',
  ':-P': '😛',
  '8-)': '😎',
  'B-)': '😎',
  ',:(': '😓',
  ',:-(': '😓',
  ',:)': '😅',
  ',:-)': '😅',
  ':s': '😒',
  ':-S': '😒',
  ':z': '😒',
  ':-Z': '😒',
  ':$': '😒',
  ':-$': '😒',
  ';)': '😉',
  ';-)': '😉',
};

const emoteVariants = Object.keys(emoteMap);
const escapedEmoteVariants = emoteVariants.map(escapeRegExp);
const regex = new RegExp(`(^|\\s)(${escapedEmoteVariants.join('|')})$`, 'g');

/**
 * Replaces basic emotes with emojis
 */
const emojiInputRule = new InputRule({
  find: regex,
  handler: ({ state, range, match }) => {
    const { tr } = state;
    const prefix = match[1];
    const emote = match[2];
    const emoji = emoteMap[emote];
    if (!emoji) {
      return null;
    }
    // Replace the matched text (including any preceding space) with the prefix and emoji.
    tr.insertText(prefix + emoji, range.from, range.to);
  },
});

/**
 * Special plugin just for damz which is a "plaintext" editor
 */
export const DaDamz = Extension.create({
  name: 'da-damz',
  priority: 1000,

  addOptions() {
    return {
      enterHandling: 'newLine',
      onImagePaste: null as ((files: File[]) => void) | null,
    };
  },

  addProseMirrorPlugins() {
    return [createPastePlugin(this.options.onImagePaste)];
  },

  addInputRules() {
    return [emojiInputRule];
  },

  /**
   * Handles the enter key UX which is:
   * There is ... menu and user can choose either enter = send or enter = newline
   * When enter = send shift+enter adds a newline
   * Also mod+enter emits submit event
   * Clients should listen for do-submit event
   */
  addKeyboardShortcuts(this) {
    return {
      Enter: () => {
        if (this.options.enterHandling === 'newLine') {
          return false; // default
        }
        // Send
        this.editor.emit('do-submit' as any, {});
        return true;
      },
      'Shift-Enter': () => {
        if (this.options.enterHandling === 'newLine') {
          return false; // default
        }
        return this.editor.chain().focus().splitBlock().run();
      },
      'Mod-Enter': () => {
        this.editor.emit('do-submit' as any, {});
        return true; // Prevent default behavior
      },
    };
  },
});
